import { isAccessDeniedCauseError } from "@skilitics/designer-access-client";
import { toast } from "@skilitics/designer-ui-components";
import { TransientError } from "@skilitics/nautilus-core-designer";

import { loggingClients } from "../logging/index.ts";
import { isFetchOrAxiosNetworkError } from "./isFetchOrAxiosNetworkError.ts";
import { getSilenceErrorLevel } from "./silenceError.ts";

// The set of errors that have already been reported.
// This can easily happen with react rendering, prefetch etc...
const reportedSet = new WeakSet();

const handle = (error: unknown) => {
  if (typeof error === "object" && error !== null) {
    if (reportedSet.has(error)) {
      return;
    }
    reportedSet.add(error);
  }

  // Currently already reported.
  if (findInCauseChain(error, (err) => err instanceof TransientError)) {
    return;
  }

  // Assume feature access denied response errors are handled at the call site.
  if (findInCauseChain(error, isAccessDeniedCauseError)) {
    return;
  }

  // Don't report or show toasts for errors that explicitly suppress it with silenceError()
  switch (findInCauseChain(error, getSilenceErrorLevel)) {
    case null:
      // continue to report error to backend and show toast
      break;
    case "none":
      console.debug("[silenced] %O", error);
      return;
    case "info":
      console.info("[silenced] %O", error);
      return;
    case "warn":
      console.warn("[silenced] %O", error);
      return;
    case "error":
      console.error("[silenced] %O", error);
      return;
  }

  // Don't report network errors to the backend.
  // isFetchOrAxiosNetworkError() already checks the cause chain.
  if (isFetchOrAxiosNetworkError(error)) {
    // but still log them to the console
    console.warn("[network error] %O", error);
  } else {
    for (const client of loggingClients) {
      client.error?.(error instanceof Error ? error : String(error));
    }
  }

  const detail = error instanceof Error ? error.message : String(error);
  const message = `Something went wrong: ${detail}`;

  // Defer the toast to avoid a React update during component render
  // Yes, error event is synchronous!
  void Promise.resolve().then(() => {
    toast.error(message, { position: "bottom-right" });
  });
};

const findInCauseChain = <T>(error: unknown, predicate: (err: unknown) => T | false | null | undefined): T | null => {
  let current: unknown = error;
  while (current) {
    const result = predicate(current);
    if (result !== false && result !== null && result !== undefined) {
      return result;
    }
    if (current instanceof Error) {
      current = current.cause;
    } else {
      break;
    }
  }
  return null;
};

window.addEventListener("error", (event) => {
  // suppress the default console error so it isn't shown twice
  // (assuming the console logging handler is enabled)
  event.preventDefault();
  handle(event.error);
});
window.addEventListener("unhandledrejection", (event) => {
  // suppress the default console error so it isn't shown twice
  // (assuming the console logging handler is enabled)
  event.preventDefault();
  handle(event.reason);
});
